import { aromaModal } from "@zenchef/styled-system/recipes";
import { Dialog, Portal } from "@ark-ui/react";
import { Box } from "@zenchef/styled-system/jsx";
import Footer from "../Footer";
import { CSSProperties, useEffect, useRef, useState } from "react";
import {
  ModalBody,
  ModalCloseTrigger,
  ModalFooter,
  ModalHeader,
  ModalProps,
  ModalRoot,
} from "./modalParts";
import Header from "../Header";
import { cx } from "@zenchef/styled-system/css";
import { token } from "@zenchef/styled-system/tokens";
import { useWindowSize } from "../../../utils";

function Modal({
  children,
  lazyMount = true,
  unmountOnExit = true,
  trapFocus = false,
  trigger,
  onOverlay = true,
  zIndex,
  title,
  headerAction,
  backAction: onBack,
  primaryAction,
  secondaryAction,
  portal,
  closeOnInteractOutside = onOverlay ?? true,
  hint,
  hintIcon = "help-question-circle",
  footer,
  footerClassName,
  contentClassName,
  className,
  maxHeight,
  indicator,
  ...props
}: ModalProps) {
  const { width: windowWidth } = useWindowSize();
  const isMobileScreen = windowWidth <= parseInt(token("breakpoints.xs"), 10);

  const [aromaModalVariantProps, restProps] = aromaModal.splitVariantProps({
    ...props,
    size: isMobileScreen ? "full" : props.size,
    stackedActions: isMobileScreen ? false : props.stackedActions,
  });

  const classes = aromaModal(aromaModalVariantProps);
  const isFullSizeVariant = aromaModalVariantProps.size === "full";
  const contentRef = useRef<HTMLDivElement>(null);
  const [hasScroll, setHasScroll] = useState(false);

  const withHeader = Boolean(
    title ?? headerAction ?? onBack ?? (isFullSizeVariant && !!hint),
  );
  const withFooter = Boolean(footer ?? primaryAction ?? secondaryAction);

  useEffect(() => {
    const checkScroll = () => {
      const el = contentRef.current;
      if (el) {
        setHasScroll(el.scrollHeight > el.clientHeight);
      }
    };

    checkScroll();
    window.addEventListener("resize", checkScroll);
    return () => window.removeEventListener("resize", checkScroll);
  }, []);

  const cssVars = {
    "--modal-z-index": zIndex,
    "--backdrop-z-index": String(Number(zIndex) - 1),
    "--modal-max-height": maxHeight ? `${maxHeight}px` : "calc(100% - 80px)",
  } as CSSProperties;

  return (
    <Dialog.Root
      lazyMount={lazyMount}
      unmountOnExit={unmountOnExit}
      trapFocus={trapFocus}
      closeOnInteractOutside={closeOnInteractOutside}
      {...restProps}
    >
      {trigger && <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>}
      <Portal {...portal}>
        {onOverlay ? (
          <Dialog.Backdrop className={classes.backdrop} style={cssVars} />
        ) : null}
        <Dialog.Positioner className={classes.positioner} style={cssVars}>
          <Dialog.Content className={cx(classes.content, className)}>
            {withHeader ? (
              <Header
                classes={classes}
                headerAction={headerAction}
                title={title}
                backAction={onBack}
                hint={isFullSizeVariant ? hint : undefined}
                hintIcon={hintIcon}
                closeTrigger={<ModalCloseTrigger />}
                indicator={indicator}
              />
            ) : (
              <ModalCloseTrigger />
            )}

            <Box
              className={cx(classes.body, contentClassName)}
              ref={contentRef}
            >
              {children}
            </Box>
            {withFooter ? (
              <Footer
                classes={classes}
                className={cx(
                  footerClassName,
                  hasScroll ? "footer-shadow" : "",
                )}
                content={footer}
                primaryAction={primaryAction}
                secondaryAction={secondaryAction}
                hint={isFullSizeVariant ? undefined : hint}
                hintIcon={hintIcon}
                fullWidth={isFullSizeVariant}
              />
            ) : null}
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}

Modal.Root = ModalRoot;
Modal.CloseTrigger = ModalCloseTrigger;
Modal.Header = ModalHeader;
Modal.Footer = ModalFooter;
Modal.Body = ModalBody;

export default Modal;
